import { useSelector } from "react-redux";
import { useAuthSelector } from "@risalabs_frontend_org/auth-wrapper";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../data-model/badgeWithIconProps";
import { ScreenNamesForBigQuery } from "../../../shared/enums/eventsData";
import { EventsName } from "../../../shared/enums/eventsName";
import {
  getAllJCodesFromAuthOnFile,
  jCodeRowLabelForAnalytics,
} from "../../pages/singleOrderLayout/utils/existingDocsToLs";
import { RootState } from "../../redux/store/store";
import { checkIfBusinessOfficeNotesAreEmpty } from "../../utils/busniessOfficeNotesHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import BadgeDropdown from "../badgeDropdown/badgeDropdown";

interface SelectableBadgeProps {
  selectedBadge: BadgeWithIconProps | null;
  badgeList: BadgeWithIconProps[];
  onClick: (id: string) => void;
  onValueChange: (value: string) => void;
  rowIndex?: number;
  fieldKey?: string;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  shouldUsePortal?: boolean;
  onDefaultBadgeClick?: (id: string) => void;
}

const SelectableBadge = (props: SelectableBadgeProps) => {
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();

  const { user } = useAuthSelector((state) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const handleBadgeSelection = (badgeId: string) => {
    const selectedBadge = props.badgeList.find((badge) => badge.id === badgeId);

    if (selectedBadge) {
      props.onClick(badgeId);

      if (props.onValueChange) {
        props.onValueChange(selectedBadge.text);
        const jCode = getAllJCodesFromAuthOnFile(
          authOnFileDetails?.auth_on_file ?? undefined,
        );
        const jCodeLabel = jCodeRowLabelForAnalytics(
          jCode,
          props.rowIndex ?? 0,
        );
        apiWorkerManager.enqueueAndProcess({
          patient_id:
            generalOrderDetails?.order?.demographics?.patient_id ?? "",
          order_id: generalOrderDetails?.order?.order_id ?? "",
          org_id: getOrgIdForFetchExternalWorklist() || "",
          user_id: user?.id ?? "",
          user_email: user?.email ?? "",
          event_name:
            EventsName.AUTH_STATUS_DROPDOWN_SELECTED +
            " (" +
            jCodeLabel +
            " - " +
            selectedBadge.text +
            ")",
          additional_data: {
            screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
            old_value: props.selectedBadge?.text ?? "",
            new_value: selectedBadge.text,
            payer_name: payerName,
            j_code: jCode?.[props.rowIndex ?? 0]?.j_code ?? "",
            bo_empty: checkIfBusinessOfficeNotesAreEmpty(
              authOnFileDetails?.auth_on_file?.reports ?? [],
            ).toString(),
            drug_name: jCode?.[props.rowIndex ?? 0]?.medicine_name ?? "",
            j_code_auth_status: jCode?.[props.rowIndex ?? 0]?.auth_status ?? "",
          },
        });
      }

      if (
        props.onTableRowUpdate &&
        props.fieldKey &&
        typeof props.rowIndex === "number"
      ) {
        const updatedBadgeValue = {
          ...props.selectedBadge,
          ...selectedBadge,
          text: selectedBadge.text,
          textColor: selectedBadge.textColor,
          bgColor: selectedBadge.bgColor,
          borderColor: selectedBadge.borderColor,
          currentStatusId: selectedBadge.id,
        };
        props.onTableRowUpdate(
          props.rowIndex,
          props.fieldKey,
          updatedBadgeValue,
        );
      }
    }
  };

  return (
    <div className="selectable-badge">
      <div className="selectable-badge--icon">
        <BadgeDropdown
          selectedBadge={props.selectedBadge}
          badgeList={props.badgeList}
          onClick={handleBadgeSelection}
          shouldUsePortal={props.shouldUsePortal}
          onDefaultBadgeClick={(id) => {
            const jCode = getAllJCodesFromAuthOnFile(
              authOnFileDetails?.auth_on_file ?? undefined,
            );
            const jCodeLabel = jCodeRowLabelForAnalytics(
              jCode,
              props.rowIndex ?? 0,
            );
            apiWorkerManager.enqueueAndProcess({
              patient_id:
                generalOrderDetails?.order?.demographics?.patient_id ?? "",
              order_id: generalOrderDetails?.order?.order_id ?? "",
              org_id: getOrgIdForFetchExternalWorklist() || "",
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              event_name:
                EventsName.AUTH_STATUS_DROPDOWN_CLICKED +
                " (" +
                jCodeLabel +
                ")",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
                j_code: jCode?.[props.rowIndex ?? 0]?.j_code ?? "",
                drug_name: jCode?.[props.rowIndex ?? 0]?.medicine_name ?? "",
                payer_name: payerName,
                j_code_auth_status:
                  jCode?.[props.rowIndex ?? 0]?.auth_status ?? "",
              },
            });
          }}
        />
      </div>
    </div>
  );
};

export default SelectableBadge;
